import React, { useState, useEffect } from 'react';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import Spinner from '../../../components/Spinner';
import { getDateTimeFromTimestamp } from '../../../utilities/datetime';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import {
  getFutureBalanceByAccount,
  FutureBalancesSelector,
} from '../../../store/exchanges/futureBalanceSlice';
import { isEmpty } from '@firebase/util';
import ChainSplit from '../components/ChainSplit';

const CustomDataTableName = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      {/* <Link to={`/exchange/${item.asset}`}>{children}</Link> */}
      {children}
    </DataTableCell>
  );
};
CustomDataTableName.displayName = DataTableCell.displayName;

/**
 * Custom Total Balance cell to display formatted date
 */
const CustomDataTableTotalBalanceCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '400' }}>{children}</span>
    </DataTableCell>
  );
};

CustomDataTableTotalBalanceCell.displayName = DataTableCell.displayName;

/**
 * Custom Free Balance cell to display formatted date
 */
const CustomDataTableFreeBalanceCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '400' }}>{children}</span>
    </DataTableCell>
  );
};

CustomDataTableFreeBalanceCell.displayName = DataTableCell.displayName;

/**
 * Custom Locked Balance cell to display formatted date
 */
const CustomDataTableLockedBalanceCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '400' }}>{children}</span>
    </DataTableCell>
  );
};

CustomDataTableLockedBalanceCell.displayName = DataTableCell.displayName;

const CustomLastTransaction = ({ children, ...props }) => {
  return (
    <DataTableCell title={children} {...props}>
      <span className="slds-m-right_xx-small">
        {/* <img src="/assets/icons/menus/transactions.svg" alt=""></img> */}
      </span>
      {children}
    </DataTableCell>
  );
};
CustomLastTransaction.displayName = DataTableCell.displayName;

const CustomDataTableCell = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      <p>{children ? getDateTimeFromTimestamp(Number(children)) : ''}</p>
    </DataTableCell>
  );
};
CustomDataTableCell.displayName = DataTableCell.displayName;

export default function MarginBalances({ account }) {
  const dispatch = useDispatch();
  const { futureBalance, mappedAssets, loading, pagination, errors, success, message } =
    useSelector(FutureBalancesSelector);
  const [sortColumn, setSortColumn] = useState('opportunityName');
  const [sortColumnDirection, setSortColumnDirection] = useState({
    opportunityName: 'asc',
  });
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [paginated, setPaginated] = useState(0);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    dispatch(getFutureBalanceByAccount({ account: account }));
  }, [dispatch]);

  useEffect(() => {
    setHasMore(true);
    if (futureBalance.length > 0) {
      setItems(futureBalance);
    }
  }, [futureBalance]);

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(true);
    setPaginated(0);
    dispatch(
      getFutureBalanceByAccount({
        account: account,
        sort_column: property,
        sort_order: sortDirection,
      })
    );

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };



  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getFutureBalanceByAccount({
              account,
              limit: url.get('limit'),
              offset: url.get('offset'),
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  if (success && showToast) {
    toast.success(message, {
      id: 'exchange-getFutureBalanceByAccount-success',
    });
    setShowToast(false);
  }

  if (!isEmpty(errors) && showToast) {
    toast.error(errors, {
      id: 'exchange-getFutureBalanceByAccount-error',
    });
    setShowToast(false);
  }

  return (
    <>
      {isPageLoading ? (
        <Spinner />
      ) : (
        <div style={{ display: 'flex' }}>
          <Toaster position="top-right" reverseOrder={false} />
          <div
            style={{
              height: `${futureBalance.length === 0 ? '100%' : 'calc(100vh - 155px)'}`,
              marginTop: '10px',
              width: '66.5%',
              position: 'relative',
            }}
          >
            <DataTable
              assistiveText={{
                actionsHeader: 'actions',
                columnSort: 'sort this column',
                columnSortedAscending: 'asc',
                columnSortedDescending: 'desc',
                selectAllRows: 'Select all rows',
                selectRow: 'Select this row',
              }}
              fixedHeader
              fixedLayout
              items={items}
              id="DataTableExample-FixedHeaders"
              onSort={handleSort}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              noRowHover
            >
              <DataTableColumn label="Name" property="asset">
                <CustomDataTableName />
              </DataTableColumn>
              <DataTableColumn label="Free Balance" property="free">
                <CustomDataTableFreeBalanceCell />
              </DataTableColumn>
              <DataTableColumn label="Locked Balance" property="locked">
                <CustomDataTableLockedBalanceCell />
              </DataTableColumn>
              <DataTableColumn label="Total Balance" property="total">
                <CustomDataTableTotalBalanceCell />
              </DataTableColumn>
            </DataTable>
            {futureBalance.length === 0 && !hasMore && (
              <div className='nodatafound' style={{marginTop: '90px'}}>
                <div>
                  <img
                    src="/assets/empty-state/empty-folder.png"
                    alt=""
                    style={{ height: '150px' }}
                  />
                  <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                    Data not available
                  </p>
                </div>
              </div>
            )}
          </div>
          <div
            className="slds-col slds-size_4-of-12"
            style={{ minHeight: '465px', height: '465px', top: '0', marginLeft: '0.6rem' }}
          >
            <ChainSplit chain_total_amounts={mappedAssets} title="Assets Split %" />
          </div>
        </div>
      )}
    </>
  );
}
